import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { peekSignedOutMessage, forgetSignedOut } from '../utils/sessionEnd';

function Login() {
  const { login, error } = useAuth();
  const { getAppName } = useConfig();
  const [loading, setLoading] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  // Set by the AuthContext interceptor when the server ended the session, and
  // removed as it is read, so it shows on that redirect and on no later visit.
  // Hidden once a sign-in is attempted.
  const [signedOutMessage, setSignedOutMessage] = useState(peekSignedOutMessage);
  useEffect(() => {
    forgetSignedOut();
  }, []);

  // Use refs for uncontrolled inputs - helps with iOS PWA issues
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // Detect PWA/standalone mode
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone
      || document.referrer.includes('android-app://');
    setIsPWA(isStandalone);

  }, []);

  // Force inputs to be interactive on mount
  useEffect(() => {
    const makeInputsInteractive = () => {
      [emailRef, passwordRef].forEach(ref => {
        if (ref.current) {
          // Remove any interfering attributes
          ref.current.removeAttribute('readonly');
          ref.current.removeAttribute('disabled');
          // Force styles
          ref.current.style.pointerEvents = 'auto';
          ref.current.style.userSelect = 'text';
          ref.current.style.webkitUserSelect = 'text';
        }
      });
    };

    // Run immediately and after a delay (for PWA load timing)
    makeInputsInteractive();
    const timer = setTimeout(makeInputsInteractive, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSignedOutMessage(null);

    const email = emailRef.current?.value || '';
    const password = passwordRef.current?.value || '';

    await login(email, password);

    setLoading(false);
  };

  // Manual focus handler for PWA mode
  const handleInputClick = (ref) => {
    if (ref.current) {
      ref.current.focus();
    }
  };

  return (
    <div className="auth-container login-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-logo">{getAppName()}</h1>
          <p className="auth-subtitle">Sign in to manage your routes</p>
          {isPWA && <p className="pwa-indicator">PWA Mode</p>}
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {signedOutMessage && !error && (
            <div className="alert alert-info signed-out-notice">
              {signedOutMessage}
            </div>
          )}
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="login-email">Email</label>
            <input
              ref={emailRef}
              type="email"
              id="login-email"
              name="email"
              className="form-input login-input"
              placeholder="you@company.com"
              required
              autoComplete="email"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              defaultValue=""
              onClick={() => handleInputClick(emailRef)}
              onTouchEnd={() => handleInputClick(emailRef)}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="login-password">Password</label>
            <input
              ref={passwordRef}
              type="password"
              id="login-password"
              name="password"
              className="form-input login-input"
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              defaultValue=""
              onClick={() => handleInputClick(passwordRef)}
              onTouchEnd={() => handleInputClick(passwordRef)}
            />
          </div>

          <div className="form-group" style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '1rem' }}>
            <Link to="/forgot-password" className="text-link">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block btn-lg"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/register">Start your free trial</Link>
          </p>
        </div>
      </div>

      <style>{`
        /* Login page specific - prevent any parent interference */
        .login-page {
          /* Ensure nothing blocks input interaction */
          position: relative;
        }

        .login-page * {
          /* Reset any inherited styles that might block touch */
          -webkit-touch-callout: default;
        }

        /* PWA debug indicator */
        .pwa-indicator {
          font-size: 0.7rem;
          color: #888;
          margin-top: 4px;
        }

        /* Critical PWA input fixes */
        .login-input {
          /* Must be 16px+ to prevent iOS zoom */
          font-size: 16px !important;

          /* Remove ALL iOS styling */
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;

          /* Force pointer events */
          pointer-events: auto !important;

          /* Force user selection */
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;

          /* Touch handling */
          touch-action: auto !important;
          -webkit-touch-callout: default !important;

          /* Ensure visibility */
          opacity: 1 !important;
          visibility: visible !important;

          /* Remove transforms that might cause issues */
          transform: none !important;

          /* Stacking */
          position: relative;
          z-index: 100;

          /* Explicit sizing */
          min-height: 50px;
          width: 100%;
          padding: 14px 16px;
          box-sizing: border-box;

          /* Visual */
          background-color: #ffffff !important;
          color: #333333 !important;
          border: 2px solid #e0e0e0;
          border-radius: 8px;

          /* Cursor */
          cursor: text !important;
          caret-color: #1e88e5;
        }

        .login-input:focus {
          outline: none;
          border-color: #1e88e5;
          box-shadow: 0 0 0 3px rgba(30, 136, 229, 0.2);
        }

        .login-input::placeholder {
          color: #999;
        }

        /* Login form - ensure no interference */
        .login-form {
          position: relative;
          z-index: 50;
        }

        .login-form .form-group {
          position: relative;
          z-index: 10;
          margin-bottom: 16px;
        }

        .login-form .form-label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          pointer-events: none;
        }

        /* iOS PWA standalone mode - extra fixes */
        @media all and (display-mode: standalone) {
          .login-input {
            /* Extra aggressive for PWA mode */
            -webkit-user-select: text !important;
            user-select: text !important;
            -webkit-touch-callout: default !important;
          }
        }

        /* iOS Safari detection */
        @supports (-webkit-touch-callout: none) {
          .login-input {
            /* iOS-specific */
            -webkit-user-select: text !important;
            user-select: text !important;
            cursor: text !important;
          }

          /* Ensure auth card doesn't block */
          .auth-card {
            -webkit-overflow-scrolling: touch;
          }
        }

        /* Chrome on iOS detection (it still uses WebKit) */
        @supports (-webkit-overflow-scrolling: touch) {
          .login-input {
            /* WebKit-based browser on iOS */
            -webkit-user-select: text !important;
            -webkit-touch-callout: default !important;
          }
        }

        /* Ensure container doesn't have overlays */
        .auth-container::before,
        .auth-container::after,
        .auth-card::before,
        .auth-card::after,
        .login-form::before,
        .login-form::after {
          content: none !important;
          display: none !important;
          pointer-events: none !important;
        }

        /* Override any global styles that might interfere */
        .login-page .auth-card {
          position: relative;
          z-index: 10;
          overflow: visible;
        }

        .login-page .form-group {
          overflow: visible;
        }

        /* Disable any tap highlight that might be interfering */
        .login-page input {
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
    </div>
  );
}

export default Login;
